import type { ComponentProps, Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { CLIENT_ENVIRONMENTS } from 'constants/Chat.constants';
import { DOCS_TWILIO_INTEGRATION } from 'constants/Docs.constants';
import { SUPPORT_EMAIL } from 'constants/EmailUs.constants';
import type { PopupActions } from 'reactjs-popup/dist/types';

import { ClientEnvironment } from '@polyai/common/constants/global.constants';
import Button from '@polyai/ui/components/atoms/Button';
import IconButton from '@polyai/ui/components/atoms/IconButton';
import { Title } from '@polyai/ui/components/atoms/LegacyText';
import LoadingIcon from '@polyai/ui/components/atoms/LoadingIcon';
import { Stack } from '@polyai/ui/components/atoms/Stack';
import Text from '@polyai/ui/components/atoms/Text';
import LegacyModal, {
  ModalButtons,
} from '@polyai/ui/components/molecules/LegacyModal';

import type { CountryCode } from 'api/resources/phoneNumber/types';
import TwilioLogo from 'assets/svg/companyLogos/twilio.svg';
import usePhoneNumber from 'hooks/usePhoneNumber';
import useTelephony from 'hooks/useTelephony';
import { useToast } from 'hooks/useToast';
import { getActionName } from 'lib/ddlogHelper';
import * as Styled from './AddNumberModal.styled';
import { ADD_NUMBER_MODAL } from './AddNumberModal.uiCopy';

enum Options {
  SIP = 'sip',
  NUMBER = 'number',
}

const telephonyAddr = process.env.NEXT_PUBLIC_TELEPHONY_ADDRESS;
const telephonyRegion = process.env.NEXT_PUBLIC_TELEPHONY_REGION;
const telephonyAddr2 = process?.env?.NEXT_PUBLIC_TELEPHONY_ADDRESS_2;

export const KAM1_SIP_ADDRESS = `sip:${telephonyAddr};region=${telephonyRegion}?X-Poly-ARI-Experiment=on&X-PolyAi-Auth-Token=`;
export const KAM2_SIP_ADDRESS = `sip:${telephonyAddr2};region=${telephonyRegion}?X-Poly-ARI-Experiment=on&X-PolyAi-Auth-Token=`;

interface PhoneNumberCardProps {
  country: string;
  phoneNumber: string;
  countryCode: CountryCode;
  clientEnv?: ClientEnvironment;
  selected?: boolean;
  setSelected?: (country: CountryCode) => void;
}

export const PhoneNumberCard = ({
  country,
  phoneNumber,
  countryCode,
  selected = false,
  setSelected = () => {},
  ...rest
}: PhoneNumberCardProps) => (
  <Styled.NumberCard
    key={country}
    selected={selected}
    title={ADD_NUMBER_MODAL.card.phoneNumber.title(country)}
    onClick={() => setSelected(countryCode)}
    {...rest}
  >
    <Styled.Description>
      {ADD_NUMBER_MODAL.card.phoneNumber.description(
        <Styled.FakeNumber>{phoneNumber}</Styled.FakeNumber>,
      )}
    </Styled.Description>
  </Styled.NumberCard>
);

export const TwilioInboundSipCard = ({
  inSettingsPage = false,
  readonly = false,
  clientEnv = ClientEnvironment.SANDBOX,
  ...rest
}: {
  inSettingsPage?: boolean;
  readonly?: boolean;
  clientEnv?: ClientEnvironment;
}) => {
  const {
    allConnectionTokens,
    createTelephonyConnector,
    isCreatingTelephonyConnector,
  } = useTelephony();
  const toast = useToast();

  useEffect(() => {
    if (!readonly && allConnectionTokens) {
      CLIENT_ENVIRONMENTS.forEach((env: ClientEnvironment) => {
        if (!allConnectionTokens[env]) {
          createTelephonyConnector({ clientEnv: env });
        }
      });
    }
    // Disabling lint to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allConnectionTokens, createTelephonyConnector, readonly]);

  const connectionToken = allConnectionTokens?.[clientEnv]?.token ?? '';
  const sipAddress = `${KAM1_SIP_ADDRESS}${connectionToken}`;
  const sipAddress2 = `${KAM2_SIP_ADDRESS}${connectionToken}`;

  const handleCopySip = async (sipAddress: string) => {
    if (connectionToken.length) {
      try {
        await navigator.clipboard.writeText(sipAddress);
        toast.success({
          title: ADD_NUMBER_MODAL.clipboard.sipAddressCopied(clientEnv),
        });
      } catch {
        toast.error({
          title: 'Could not copy SIP. Try again!',
        });
      }
    }
  };

  return (
    <Styled.TwilioSipCard
      $hasBorder={!inSettingsPage}
      avatar={<TwilioLogo />}
      fullWidth={!inSettingsPage}
      title={
        readonly
          ? ADD_NUMBER_MODAL.card.twilio.title.default
          : ADD_NUMBER_MODAL.card.twilio.title.connect
      }
      {...rest}
    >
      <Styled.TwilioSipCardText>
        {inSettingsPage
          ? ''
          : ADD_NUMBER_MODAL.card.twilio.description.nonSettingsPage}
        {!readonly &&
          ADD_NUMBER_MODAL.card.twilio.description.instruction(
            (text: string) => (
              <Styled.TwilioSipCardHelpLink
                data-dd-action={getActionName(
                  'Phone Numbers',
                  'open',
                  'external number documentation',
                )}
                href={DOCS_TWILIO_INTEGRATION}
                rel="noreferrer"
                target="_blank"
              >
                {text}
              </Styled.TwilioSipCardHelpLink>
            ),
          )}
      </Styled.TwilioSipCardText>
      <Styled.TwilioSipCardContentWrapper>
        {!readonly && connectionToken && (
          <>
            <Styled.ConnectionTokenWrapper>
              <Title>{sipAddress}</Title>
              <IconButton
                data-dd-action={getActionName(
                  'Phone Numbers',
                  'copy',
                  'SIP code',
                )}
                data-test-id="copy-sip-button"
                disabled={!connectionToken}
                onClick={() => handleCopySip(sipAddress)}
              >
                <Styled.CopyTokenIcon color="iconSecondary" />
              </IconButton>
            </Styled.ConnectionTokenWrapper>
            <Styled.ConnectionTokenWrapper>
              <Title>{sipAddress2}</Title>
              <IconButton
                data-dd-action={getActionName(
                  'Phone Numbers',
                  'copy',
                  'SIP code',
                )}
                data-test-id="copy-sip-button"
                disabled={!connectionToken}
                onClick={() => handleCopySip(sipAddress2)}
              >
                <Styled.CopyTokenIcon color="iconSecondary" />
              </IconButton>
            </Styled.ConnectionTokenWrapper>
          </>
        )}
        {!readonly && (isCreatingTelephonyConnector || !connectionToken) && (
          <>
            <Styled.ConnectionTokenWrapper $whiteBg>
              <Stack align="center" spacing="xs1" style={{ width: '100%' }}>
                <LoadingIcon />{' '}
                <Text color="textSecondary">
                  {ADD_NUMBER_MODAL.card.twilio.loading}
                </Text>
              </Stack>
            </Styled.ConnectionTokenWrapper>
            <Styled.ConnectionTokenWrapper $whiteBg />
          </>
        )}
      </Styled.TwilioSipCardContentWrapper>
    </Styled.TwilioSipCard>
  );
};

export const AVAILABLE_NUMBERS: {
  country: string;
  fakeNumber: string;
  countryCode: CountryCode;
}[] = [
  {
    country: 'US',
    fakeNumber: '+1 *** *** ****',
    countryCode: 'US',
  },
  {
    country: 'UK',
    fakeNumber: '+44 **** *** ***',
    countryCode: 'GB',
  },
];

interface ModalContentProps {
  createAgentRedirect: boolean;
  closeModal: () => void;
  setStage: Dispatch<SetStateAction<Options | undefined>>;
  clientEnv?: ClientEnvironment;
}

type TwilioSipContentProps = Omit<ModalContentProps, 'createAgentRedirect'>;

const BuyPhoneNumberContent = ({
  createAgentRedirect,
  closeModal,
  setStage,
  clientEnv = ClientEnvironment.SANDBOX,
}: ModalContentProps) => {
  const [selectedCountry, setSelectedCountry] = useState<
    CountryCode | undefined
  >();

  const { buyPhoneNumber } = usePhoneNumber();

  const buyNumber = useCallback(() => {
    if (!selectedCountry) {
      return;
    }

    buyPhoneNumber({
      countryCode: selectedCountry,
      clientEnv,
    });

    datadogRum.addAction(
      getActionName('Phone Numbers', 'create', 'number', 'complete'),
      {
        country: selectedCountry,
      },
    );

    closeModal();
  }, [buyPhoneNumber, selectedCountry, closeModal, clientEnv]);

  const setSelected = (country: CountryCode) => {
    setSelectedCountry(country);
  };

  return (
    <>
      <Title>
        {createAgentRedirect
          ? ADD_NUMBER_MODAL.buyPhoneNumber.title.fromCreateAssistant
          : ADD_NUMBER_MODAL.buyPhoneNumber.title.default}
      </Title>
      <Styled.Content>
        <Styled.CardContainer>
          {AVAILABLE_NUMBERS.map(({ country, fakeNumber, countryCode }) => {
            const selected = selectedCountry === countryCode;
            return (
              <PhoneNumberCard
                key={`${country}-${fakeNumber}`}
                country={country}
                countryCode={countryCode}
                phoneNumber={fakeNumber}
                selected={selected}
                setSelected={setSelected}
              />
            );
          })}
        </Styled.CardContainer>
      </Styled.Content>
      <ModalButtons
        backButtonText={ADD_NUMBER_MODAL.buyPhoneNumber.button.cancel}
        goBack={createAgentRedirect ? closeModal : undefined}
      >
        {createAgentRedirect ? (
          <Button
            label={ADD_NUMBER_MODAL.buyPhoneNumber.button.back}
            variant="SECONDARY"
            onClick={() => setStage(undefined)}
          />
        ) : (
          <Button
            label={ADD_NUMBER_MODAL.buyPhoneNumber.button.cancel}
            variant="SECONDARY"
            onClick={closeModal}
          />
        )}
        <Button
          data-dd-action={getActionName(
            'Phone Numbers',
            'create',
            'number',
            'start',
          )}
          data-test-id="buy-number-button"
          disabled={!selectedCountry}
          label={ADD_NUMBER_MODAL.buyPhoneNumber.button.done}
          onClick={buyNumber}
        />
      </ModalButtons>
    </>
  );
};

const NumberSipOptionsContent = ({
  createAgentRedirect,
  closeModal,
  setStage,
}: ModalContentProps) => {
  const [selected, setSelected] = useState<Options | undefined>();

  return (
    <>
      <Title>
        {createAgentRedirect
          ? ADD_NUMBER_MODAL.telephonyConfig.title.fromCreateAssistant
          : ADD_NUMBER_MODAL.telephonyConfig.title.default}
      </Title>
      <Styled.ModalSubtext>
        {ADD_NUMBER_MODAL.telephonyConfig.description}
      </Styled.ModalSubtext>
      <Styled.CardContainer>
        <Styled.NumberCard
          description={ADD_NUMBER_MODAL.telephonyConfig.options.buy.description}
          selected={selected === Options.NUMBER}
          title={ADD_NUMBER_MODAL.telephonyConfig.options.buy.title}
          onClick={() => setSelected(Options.NUMBER)}
        />
        <Styled.NumberCard
          description={
            ADD_NUMBER_MODAL.telephonyConfig.options.twilio.description
          }
          selected={selected === Options.SIP}
          title={ADD_NUMBER_MODAL.telephonyConfig.options.twilio.title}
          onClick={() => setSelected(Options.SIP)}
        />
      </Styled.CardContainer>
      <Styled.ButtonsGroup>
        <Button
          label={
            createAgentRedirect
              ? ADD_NUMBER_MODAL.telephonyConfig.button.cancel
                  .fromCreateAssistant
              : ADD_NUMBER_MODAL.telephonyConfig.button.cancel.default
          }
          variant="SECONDARY"
          onClick={closeModal}
        />
        <Button
          disabled={!selected}
          label={ADD_NUMBER_MODAL.telephonyConfig.button.confirm}
          onClick={() => setStage(selected)}
        />
      </Styled.ButtonsGroup>
    </>
  );
};

const TwilioInboundSipContent = ({
  closeModal,
  setStage,
}: TwilioSipContentProps) => {
  return (
    <>
      <Styled.TwilioSipCardTitle>
        {ADD_NUMBER_MODAL.connectExistingNumber.title}
      </Styled.TwilioSipCardTitle>
      <Styled.ModalSubtext>
        {ADD_NUMBER_MODAL.connectExistingNumber.description(
          <Styled.SupportEmail>{SUPPORT_EMAIL}</Styled.SupportEmail>,
        )}
      </Styled.ModalSubtext>
      <TwilioInboundSipCard readonly={false} />
      <Styled.ButtonsGroup
        backButtonText={ADD_NUMBER_MODAL.connectExistingNumber.button.cancel}
        goBack={closeModal}
      >
        <Button
          label={ADD_NUMBER_MODAL.connectExistingNumber.button.back}
          variant="SECONDARY"
          onClick={() => setStage(undefined)}
        />
        <Button
          data-dd-action={getActionName(
            'Phone Numbers',
            'use',
            'existing number',
          )}
          label={ADD_NUMBER_MODAL.connectExistingNumber.button.done}
          onClick={closeModal}
        />
      </Styled.ButtonsGroup>
    </>
  );
};

interface AddNumberModalProps {
  trigger?: ComponentProps<typeof LegacyModal>['trigger'];
  showTelephonySelection?: boolean;
  createAgentRedirect?: boolean;
  clientEnv?: ClientEnvironment;
  open?: boolean;
}

const AddNumberModal = ({
  trigger = undefined,
  showTelephonySelection = false,
  createAgentRedirect = false,
  clientEnv = ClientEnvironment.SANDBOX,
  open = false,
}: AddNumberModalProps) => {
  const modalRef = useRef<PopupActions>(null);
  const [stage, setStage] = useState<Options | undefined>();

  const closeModal = () => {
    setStage(undefined);
    modalRef?.current?.close();
  };

  if (showTelephonySelection) {
    return (
      <LegacyModal
        ref={modalRef}
        open={open}
        size="md"
        trigger={trigger}
        closeOnDocumentClick
        onClose={closeModal}
      >
        {!stage && (
          <NumberSipOptionsContent
            closeModal={closeModal}
            createAgentRedirect={createAgentRedirect}
            setStage={setStage}
          />
        )}
        {stage === Options.NUMBER && (
          <BuyPhoneNumberContent
            closeModal={closeModal}
            createAgentRedirect={createAgentRedirect}
            setStage={setStage}
          />
        )}
        {stage === Options.SIP && (
          <TwilioInboundSipContent
            closeModal={closeModal}
            setStage={setStage}
          />
        )}
      </LegacyModal>
    );
  }

  return (
    <LegacyModal
      ref={modalRef}
      size="md"
      trigger={trigger}
      closeOnDocumentClick
      onClose={closeModal}
    >
      <BuyPhoneNumberContent
        clientEnv={clientEnv}
        closeModal={closeModal}
        createAgentRedirect={createAgentRedirect}
        setStage={setStage}
      />
    </LegacyModal>
  );
};

export default AddNumberModal;
